import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LegalPageShell from './LegalPageShell';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const ROLES = [
  { value: 'client', label: 'Client' },
  { value: 'owner', label: 'Owner' },
  { value: 'operator', label: 'Field Expert (operator)' },
];

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length >= 10) return digits.slice(-10);
  return digits;
}

function isValidIndianMobile(value) {
  return /^[6-9]\d{9}$/.test(normalizePhone(value));
}

export default function DeleteAccountPage() {
  const [step, setStep] = useState('info'); // info | otp | done
  const [role, setRole] = useState('client');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [blockers, setBlockers] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [successMsg, setSuccessMsg] = useState('');

  const resetMessages = () => {
    setErrorMsg('');
    setInfoMsg('');
    setBlockers([]);
    setWarnings([]);
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    if (loading) return;
    resetMessages();
    const normalized = normalizePhone(phone);
    if (!isValidIndianMobile(normalized)) {
      setErrorMsg('Enter a valid 10-digit Indian mobile number.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/account-deletion/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalized, role }),
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 429) {
        setErrorMsg(payload.error || 'Too many requests. Please wait and try again.');
        return;
      }
      if (response.status === 400) {
        setErrorMsg(payload.error || 'Valid role and 10-digit mobile number are required');
        return;
      }
      // 200 always uses a generic message (no account enumeration)
      setInfoMsg(payload.message || 'If an account exists, OTP was sent to the registered mobile number.');
      setPhone(normalized);
      setStep('otp');
    } catch (_err) {
      setErrorMsg('Could not request OTP. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (loading) return;
    resetMessages();
    if (confirmation.trim() !== 'DELETE') {
      setErrorMsg('Type DELETE (all caps) to confirm.');
      return;
    }
    if (!/^\d{6}$/.test(String(otp).trim())) {
      setErrorMsg('Enter the 6-digit OTP from SMS.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/account-deletion/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: normalizePhone(phone),
          role,
          otp: String(otp).trim(),
          confirmation: 'DELETE',
        }),
      });
      const payload = await response.json().catch(() => ({}));

      if (response.status === 409) {
        setErrorMsg(payload.error || 'Account cannot be deleted yet');
        setBlockers(Array.isArray(payload.blockers) ? payload.blockers : []);
        setWarnings(Array.isArray(payload.warnings) ? payload.warnings : []);
        return;
      }
      if (response.status === 429) {
        setErrorMsg(payload.error || 'Too many requests. Please wait and try again.');
        return;
      }
      if (response.status === 400) {
        setErrorMsg(payload.error || 'Invalid request');
        return;
      }
      if (response.status >= 500) {
        setErrorMsg(payload.error || 'Account deletion failed');
        return;
      }
      if (response.ok && payload.deleted) {
        setSuccessMsg(payload.message || 'If an eligible account matched, it has been deleted.');
        setWarnings(Array.isArray(payload.warnings) ? payload.warnings : []);
        setStep('done');
        return;
      }
      setErrorMsg(payload.error || 'Could not complete deletion. Please try again.');
    } catch (_err) {
      setErrorMsg('Could not complete deletion. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalPageShell title="Delete your account" meta="Development Express — account deletion">
      {step === 'done' ? (
        <section style={s.card}>
          <h2 style={s.h2}>Request completed</h2>
          <p style={s.body}>{successMsg}</p>
          {warnings.length > 0 && (
            <div style={s.warnBox}>
              <p style={s.warnTitle}>Notes</p>
              <ul style={s.list}>
                {warnings.map((w) => (
                  <li key={w.code || w.message}>{w.message || w.code}</li>
                ))}
              </ul>
            </div>
          )}
          <p style={{ ...s.body, marginTop: 16 }}>
            You can create a new Development Express account later with the same mobile number if you wish.
          </p>
          <p style={{ marginTop: 20 }}>
            <Link to="/privacy" style={s.link}>Privacy Policy</Link>
            <span style={s.sep}> · </span>
            <Link to="/contact" style={s.link}>Contact</Link>
          </p>
        </section>
      ) : (
        <>
          <section style={{ marginBottom: 20 }}>
            <h2 style={s.h2}>What this does</h2>
            <p style={s.body}>
              Deleting your Development Express account closes sign-in for that role and removes personal
              contact details from your profile (phone, bank details, profile photo, uploaded KYC document
              files). Your account is soft-anonymized — we do not erase history that the law or tax rules
              require us to keep.
            </p>
          </section>

          <section style={{ marginBottom: 20 }}>
            <h2 style={s.h2}>What is retained</h2>
            <p style={s.body}>
              Financial, invoice, GST, TDS, booking and wallet ledger records are retained for as long
              as required under applicable Indian law (including the Income-tax Act, 1961 and the
              CGST Act, 2017) — currently up to 8 years — and then deleted. PAN, GSTIN and legal/business name are retained with those records for the same
              legal purpose. KYC documents (PAN/Aadhaar/GST certificate images), profile photo, bank
              details and saved addresses are deleted when the account is deleted, unless required for
              an ongoing dispute or legal proceeding. Retained records are not sold or used for marketing.
            </p>
          </section>

          <section style={{ marginBottom: 20 }}>
            <h2 style={s.h2}>When deletion is blocked</h2>
            <p style={s.body}>
              You must clear these first: wallet balance greater than zero; pending withdrawals or top-ups;
              pending settlement transfers; active or disputed bookings; unpaid RA bills. Field Experts
              (operators) cannot delete while assigned to an in-flight booking. Open support grievances
              are a warning only — they do not block deletion.
            </p>
          </section>

          <section style={{ marginBottom: 24 }}>
            <h2 style={s.h2}>Prefer the app?</h2>
            <p style={s.body}>
              Signed-in users can also delete from the Development Express mobile app:{' '}
              <strong style={{ color: '#e8e0d0' }}>Profile → Delete my account</strong>
              {' '}(OTP to your registered mobile + type DELETE).
            </p>
          </section>

          {step === 'info' && (
            <section style={s.card}>
              <h2 style={s.h2}>Request deletion by SMS OTP</h2>
              <p style={s.note}>
                We send a one-time code only if a matching account may exist. Responses are generic so
                we do not reveal whether a number is registered.
              </p>
              {errorMsg && <p style={s.errorMsg}>{errorMsg}</p>}
              <form onSubmit={requestOtp} style={s.form}>
                <label style={s.label}>
                  Account role
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    style={s.input}
                    disabled={loading}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </label>
                <label style={s.label}>
                  Registered mobile number
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="10-digit mobile"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={14}
                    style={s.input}
                    disabled={loading}
                    required
                  />
                </label>
                <button type="submit" style={s.submitBtn} disabled={loading}>
                  {loading ? 'Sending…' : 'Send OTP'}
                </button>
              </form>
            </section>
          )}

          {step === 'otp' && (
            <section style={s.card}>
              <h2 style={s.h2}>Confirm with OTP</h2>
              <p style={s.note}>{infoMsg}</p>
              <p style={s.note}>
                Role: <strong style={{ color: '#e8e0d0' }}>{role}</strong>
                {' · '}Phone ending {normalizePhone(phone).slice(-4)}
              </p>
              {errorMsg && <p style={s.errorMsg}>{errorMsg}</p>}
              {blockers.length > 0 && (
                <div style={s.blockBox}>
                  <p style={s.blockTitle}>Resolve these before deleting</p>
                  <ul style={s.list}>
                    {blockers.map((b) => (
                      <li key={b.code || b.message}>
                        <strong style={{ color: '#e8e0d0' }}>{b.code ? `${b.code}: ` : ''}</strong>
                        {b.message || b.code}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {warnings.length > 0 && (
                <div style={s.warnBox}>
                  <p style={s.warnTitle}>Warnings (do not block)</p>
                  <ul style={s.list}>
                    {warnings.map((w) => (
                      <li key={w.code || w.message}>{w.message || w.code}</li>
                    ))}
                  </ul>
                </div>
              )}
              <form onSubmit={confirmDelete} style={s.form}>
                <label style={s.label}>
                  6-digit OTP from SMS
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    style={s.input}
                    disabled={loading}
                    required
                  />
                </label>
                <label style={s.label}>
                  Type DELETE to confirm
                  <input
                    type="text"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder="DELETE"
                    style={s.input}
                    disabled={loading}
                    required
                  />
                </label>
                <div style={s.btnRow}>
                  <button type="submit" style={s.dangerBtn} disabled={loading}>
                    {loading ? 'Deleting…' : 'Delete my account'}
                  </button>
                  <button
                    type="button"
                    style={s.secondaryBtn}
                    disabled={loading}
                    onClick={() => {
                      resetMessages();
                      setOtp('');
                      setConfirmation('');
                      setStep('info');
                    }}
                  >
                    Back
                  </button>
                </div>
              </form>
            </section>
          )}
        </>
      )}
    </LegalPageShell>
  );
}

const s = {
  h2: { color: '#c9a84c', fontSize: '14px', margin: '0 0 8px' },
  body: { color: '#b8c0cc', fontSize: '13px', margin: 0, whiteSpace: 'pre-line' },
  note: { color: '#8896a8', fontSize: '12px', margin: '0 0 12px' },
  card: {
    marginTop: '8px',
    padding: '20px',
    background: '#030810',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: '8px',
    boxSizing: 'border-box',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#8896a8',
    fontSize: '12px',
  },
  input: {
    background: 'rgba(15,32,64,0.8)',
    border: '1px solid rgba(201,168,76,0.3)',
    borderRadius: '8px',
    color: '#e8e0d0',
    fontSize: '13px',
    padding: '8px 12px',
    outline: 'none',
    boxSizing: 'border-box',
    width: '100%',
  },
  submitBtn: {
    marginTop: '4px',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #c9a84c, #8b6914)',
    color: '#0a1628',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  dangerBtn: {
    marginTop: '4px',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #c45c5c, #8b3030)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer',
  },
  secondaryBtn: {
    marginTop: '4px',
    padding: '10px 16px',
    background: 'transparent',
    color: '#c9a84c',
    border: '1px solid rgba(201,168,76,0.4)',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
  },
  btnRow: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' },
  errorMsg: { color: '#e8a0a0', fontSize: '13px', margin: '0 0 12px' },
  blockBox: {
    marginBottom: 12,
    padding: 12,
    borderRadius: 8,
    border: '1px solid rgba(232,160,160,0.45)',
    background: 'rgba(120,40,40,0.25)',
  },
  blockTitle: { color: '#e8a0a0', fontSize: 12, fontWeight: 700, margin: '0 0 8px' },
  warnBox: {
    marginBottom: 12,
    padding: 12,
    borderRadius: 8,
    border: '1px solid rgba(201,168,76,0.35)',
    background: 'rgba(201,168,76,0.08)',
  },
  warnTitle: { color: '#c9a84c', fontSize: 12, fontWeight: 700, margin: '0 0 8px' },
  list: { margin: 0, paddingLeft: 18, color: '#b8c0cc', fontSize: 13, lineHeight: 1.5 },
  link: { color: '#c9a84c' },
  sep: { color: '#556' },
};
